import {
  type CouponInputFormik,
  DurationType,
  type PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseEntity } from "@modules/common/pauseObject/entities/pauseObjectEntity"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { TrialSubscriptionEntity } from "@modules/common/trialSubscription/entities/trailSubscriptionEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { LightInvoiceEntity } from "@modules/finances/entities/lightInvoiceEntity"
import { type PaymentError } from "@modules/finances/types/stripeErrorTypes"
import { Language } from "@modules/language/utils/languageUtils"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { type ProgramBundleSubscriberDto } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberDto"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import { type HUImage } from "@modules/types/imageTypes"
import { colors } from "@modules/ui/styles/colors"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"

export type ProgramBundleSubscriberState = Omit<
  ProgramBundleSubscriberEntity,
  | "getProgramSubscriptionStatusInfos"
  | "getPause"
  | "canSubscriptionBePaused"
  | "canApplyCoupon"
  | "canDeleteSubscriber"
  | "getCoupon"
  | "canSubscriberAccessToChat"
  | "canUserFollowBundlePrograms"
  | "getWordingCouponPriceAfterDiscount"
  | "getWordingReductionForPromoCode"
  | "getWordingReductionForDiscount"
  | "getWordingCouponDate"
  | "getWordingTrialDate"
  | "getWordingPause"
  | "getWordingPrice"
>

export class ProgramBundleSubscriberEntity {
  id: string
  username: string
  firstname: string
  lastname: string
  avatar?: HUImage
  subscriptionStatus: ProgramSubscriptionStatus
  subscriptionId?: string
  programBundleSubscriptionId: string
  periodEndDate: Date | null
  subscriptionDate: Date
  paidPrice: PriceObjectEntity | null
  referencePrice: PriceObjectEntity | null
  usedPromoCode?: LightPromoCodeEntity
  coupon?: CouponEntity
  pause?: PauseEntity
  trial?: TrialSubscriptionEntity
  internalNote?: string
  email?: string
  nationality?: string
  error?: PaymentError
  programBundle?: ProgramBundleEntity
  lastInvoices: LightInvoiceEntity[] | null
  nextPayment: PriceObjectEntity | null
  nextPaymentDate: Date | null

  private constructor(programBundleSubscriber: ProgramBundleSubscriberState) {
    this.id = programBundleSubscriber.id
    this.username = programBundleSubscriber.username
    this.firstname = programBundleSubscriber.firstname
    this.lastname = programBundleSubscriber.lastname
    this.avatar = programBundleSubscriber.avatar
    this.subscriptionStatus = programBundleSubscriber.subscriptionStatus
    this.subscriptionId = programBundleSubscriber.subscriptionId
    this.programBundleSubscriptionId = programBundleSubscriber.programBundleSubscriptionId
    this.periodEndDate = programBundleSubscriber.periodEndDate
    this.subscriptionDate = programBundleSubscriber.subscriptionDate
    this.paidPrice = programBundleSubscriber.paidPrice
    this.referencePrice = programBundleSubscriber.referencePrice
    this.usedPromoCode = programBundleSubscriber.usedPromoCode
    this.coupon = programBundleSubscriber.coupon
    this.pause = programBundleSubscriber.pause
    this.trial = programBundleSubscriber.trial
    this.internalNote = programBundleSubscriber.internalNote
    this.email = programBundleSubscriber.email
    this.nationality = programBundleSubscriber.nationality
    this.error = programBundleSubscriber.error
    this.programBundle = programBundleSubscriber.programBundle
    this.lastInvoices = programBundleSubscriber.lastInvoices
    this.nextPayment = programBundleSubscriber.nextPayment
    this.nextPaymentDate = programBundleSubscriber.nextPaymentDate
  }

  static fromItemDto(dto: ProgramBundleSubscriberDto): ProgramBundleSubscriberEntity {
    return new ProgramBundleSubscriberEntity({
      id: dto.id,
      username: dto.username,
      firstname: dto.firstname,
      lastname: dto.lastname,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      subscriptionStatus: dto.subscriptionStatus,
      subscriptionId: dto.subscriptionId ?? undefined,
      programBundleSubscriptionId: dto.programBundleSubscriptionId,
      subscriptionDate: new Date(dto.subscriptionDate),
      periodEndDate: dto.periodEndDate ? new Date(dto.periodEndDate) : null,
      paidPrice: dto.paidPrice ? PriceObjectEntity.fromDto(dto.paidPrice) : null,
      referencePrice: dto.referencePrice ? PriceObjectEntity.fromDto(dto.referencePrice) : null,
      usedPromoCode: dto.usedPromoCode ? LightPromoCodeEntity.fromDto(dto.usedPromoCode) : undefined,
      coupon: dto.coupon ? CouponEntity.fromDto(dto.coupon) : undefined,
      pause: dto.pause ? PauseEntity.fromDto(dto.pause) : undefined,
      trial: dto.trial ? TrialSubscriptionEntity.fromDto(dto.trial) : undefined,
      email: dto.email ?? undefined,
      nationality: dto.nationality ?? undefined,
      internalNote: dto.internalNote ?? undefined,
      error: dto.error ?? undefined,
      programBundle: ProgramBundleEntity.fromDto(dto.programBundle),
      lastInvoices: dto.lastInvoices ? dto.lastInvoices.map((invoice) => LightInvoiceEntity.fromDto(invoice)) : null,
      nextPayment: dto.nextPayment ? PriceObjectEntity.fromDto(dto.nextPayment) : null,
      nextPaymentDate: dto.nextPaymentDate ? new Date(dto.nextPaymentDate) : null,
    })
  }

  getWordingCouponDate(): string {
    if (!this.coupon) return ""

    if (this.coupon.duration === null) {
      return word("boxSubscription.coupon.date.unlimited", {
        date: formatDateWithHyphen(dayjs(this.coupon.createdAt).format("L")),
      })
    }

    return word("boxSubscription.coupon.date.durationInMonths", {
      dateStart: formatDateWithHyphen(dayjs(this.coupon.createdAt).format("L")),
      dateEnd: formatDateWithHyphen(dayjs(this.coupon.createdAt).add(this.coupon.duration, "months").format("L")),
    })
  }

  // The coupon is calculated after calculating the promo code if there is one
  getWordingCouponPriceAfterDiscount(language: Language): string {
    if (!this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    price =
      this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value

    return formatCurrency(price, this.referencePrice.currency, language)
  }

  getWordingReductionForDiscount(language: Language): string {
    if (!this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    const amountOfDiscount =
      this.coupon.type === CouponType.PERCENT ? (price * this.coupon.value) / 100 : this.coupon.value

    return formatCurrency(amountOfDiscount, this.referencePrice.currency, language)
  }

  getWordingReductionForPromoCode(language: Language): string {
    if (!this.referencePrice || !this.usedPromoCode) {
      return ""
    }

    const amountOfDiscount =
      this.usedPromoCode.type === CouponType.PERCENT
        ? (this.referencePrice.value * this.usedPromoCode.value) / 100
        : this.usedPromoCode.value

    return formatCurrency(amountOfDiscount, this.referencePrice.currency, language)
  }

  getProgramSubscriptionStatusInfos(): { color: string; label: string; information?: string } {
    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.ACTIVE:
      case ProgramSubscriptionStatus.ACTIVE_BUNDLE:
        return { color: colors.green500, label: word("program.subscribers.subscriptionDetails.status.active.label") }
      case ProgramSubscriptionStatus.INACTIVE:
      case ProgramSubscriptionStatus.INACTIVE_BUNDLE:
        return {
          color: colors.red500,
          label: `${word("program.subscribers.subscriptionDetails.status.inactive.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
        return {
          color: colors.red500,
          label: `${word("program.subscribers.subscriptionDetails.status.pending_cancellation.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_PAYMENT:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_payment.label"),
        }
      case ProgramSubscriptionStatus.PAUSE:
        return {
          color: colors.yellow500,
          label: `${word("program.subscribers.subscriptionDetails.status.paused.label")} - ${dayjs(this.pause?.pausedAt).format("L")}`,
          information: this.getWordingPause(),
        }
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.payment_in_error.label"),
          information: word("program.subscribers.subscriptionDetails.status.payment_in_error.information"),
        }
      case ProgramSubscriptionStatus.TRIALING:
        return {
          color: colors.green500,
          label: word("program.subscribers.subscriptionDetails.status.trialing.label"),
          information: this.getWordingTrialDate(),
        }
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.trial_pending_cancellation.label"),
          information: this.getWordingTrialDate(),
        }
      case ProgramSubscriptionStatus.PENDING_REQUEST:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_request.label"),
        }
    }
  }

  getWordingTrialDate(): string {
    if (!this.trial) return ""

    const totalDays = dayjs(this.trial.endDate).diff(dayjs(this.trial.startDate), "days")
    const remainingDays = dayjs(this.trial.endDate).diff(dayjs(), "days")
    const daysUsed = totalDays - remainingDays

    return word("program.subscribers.subscriptionDetails.status.trialing.information", {
      daysUsed,
      totalDays,
      date: dayjs(this.trial.endDate).format("L"),
    })
  }

  getWordingPause(): string {
    if (!this.pause) return ""

    const pausedAt = dayjs(this.pause.pausedAt).format("L")

    if (!this.pause.resumeAt) {
      return word("program.subscribers.subscriptionDetails.status.paused.unlimited", {
        date: dayjs(this.pause.pausedAt).format("L"),
      })
    }

    const totalMonths = Math.ceil(dayjs(this.pause.resumeAt).diff(dayjs(this.pause.pausedAt), "month", true))
    const monthsUsed = dayjs(this.pause.pausedAt).diff(dayjs(), "month")
    const resumeAt = dayjs(this.pause.resumeAt).format("L")
    return word("program.subscribers.subscriptionDetails.status.paused.limited", {
      monthsUsed,
      totalMonths,
      pausedAt,
      resumeAt,
    })
  }

  getWordingPrice(): string {
    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAUSE:
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return word("program.price.noFuturePayment")
      case ProgramSubscriptionStatus.TRIALING:
        return word("program.price.free")
      default:
        return ""
    }
  }

  getPause(): PauseInputFormik {
    if (!this.pause) {
      return {
        durationType: DurationType.MONTHLY,
        months: 1,
      }
    }

    if (this.pause.resumeAt === null && this.pause.pausedAt) {
      return {
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }

    const pausedAt = dayjs(this.pause.pausedAt)
    const resumeAt = dayjs(this.pause.resumeAt)
    const months = resumeAt.diff(pausedAt, "month")

    return {
      durationType: months > 0 ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: months > 0 ? months : 1,
    }
  }

  getCoupon(): CouponInputFormik {
    if (!this.coupon) {
      return {
        type: CouponType.PERCENT,
        value: 0,
        durationType: DurationType.MONTHLY,
        months: 0,
      }
    }
    return {
      type: this.coupon.type,
      value: this.coupon.value,
      durationType: this.coupon.duration ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: this.coupon.duration || null,
    }
  }

  canUserFollowBundlePrograms(): boolean {
    return (
      this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
      this.subscriptionStatus === ProgramSubscriptionStatus.PAYMENT_IN_ERROR ||
      this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ||
      this.subscriptionStatus === ProgramSubscriptionStatus.TRIALING ||
      this.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION
    )
  }

  canSubscriptionBePaused(): boolean {
    return (
      !!this.paidPrice &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)
    )
  }

  canApplyCoupon(): boolean {
    return (
      !!this.paidPrice &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
        this.subscriptionStatus === ProgramSubscriptionStatus.TRIALING ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAYMENT_IN_ERROR)
    )
  }

  canDeleteSubscriber(): boolean {
    return true
  }

  canSubscriberAccessToChat(): boolean {
    return (
      this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
      this.subscriptionStatus === ProgramSubscriptionStatus.PAYMENT_IN_ERROR ||
      this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ||
      this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
      this.subscriptionStatus === ProgramSubscriptionStatus.TRIALING ||
      this.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION
    )
  }
}
